// packages/apps/pro-studio/src/stores/native-cell-value.ts
//
// PSTUDIO-2821: pure value helpers shared by the native cell renderer
// (cell.tsx) and the store's draft-commit path (table-store.ts
// commitCellEditing). No React / MobX imports so they stay trivially unit
// testable and free of the heavy store boundary.

// Columns whose stored value is an array but which edit as a single-line
// <input>, joined for display / split for save on a column-specific separator.
// Mirrors the cell renderer and the original DS cells (Output / WhatsNext /
// Variables / NextNodes).
import { toNodeNumberCell } from './utils/node-number';

export const ARRAY_INPUT_SEPARATORS: Record<string, string> = {
  nextNodes: ',',
  output: ',',
  variables: ',',
  whatsNext: '|',
};

// Stable string form of any stored cell value, matching what the uncontrolled
// control shows as its defaultValue. Single source of truth for the renderer
// and the commit path.
export function stringifyValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v)))
      .join(', ');
  }
  return JSON.stringify(value);
}

// PSTUDIO-4081: the text an ARRAY column's control shows for a stored value.
//
// An array joins on the column's own separator, so it round-trips through
// `parseNativeCellRaw` unchanged. Anything else falls back to `stringifyValue`
// instead of the empty string the cell used to render: the row can legitimately
// be holding the RAW typed text while a panel edit is still open (PSTUDIO-3882
// writes the draft per keystroke and canonicalizes it only on commit), and
// blanking the cell hid that value in the grid while the side panel showed it.
// A cell the user cannot see is a cell the user copies without knowing what it
// carries — which is how a desynced row reached a cross-solution paste and lost
// the whole batch to a rejected upsert.
export function arrayCellText(value: unknown, separator: string): string {
  return Array.isArray(value) ? value.join(separator) : stringifyValue(value);
}

// Parse a control's raw string back into the typed value to persist, by column.
//   nodeNumber -> canonical string, or the text as typed (PSTUDIO-4453; see
//                 toNodeNumberCell); empty -> undefined, as Delete clears it
//   array cols -> string[] (split on the column separator)
//   else       -> the raw string (textarea + default text)
export function parseNativeCellRaw(columnId: string, raw: string): unknown {
  if (columnId === 'nodeNumber') {
    return toNodeNumberCell(raw) ?? undefined;
  }
  const sep = ARRAY_INPUT_SEPARATORS[columnId];
  if (sep) {
    return raw === '' ? [] : raw.split(sep);
  }
  return raw;
}

// True when the raw control string is equivalent to the stored value — i.e. the
// user did not actually change anything, so no write / undo entry is needed.
// Mirrors the per-control comparisons the old blur handlers used in cell.tsx.
export function nativeCellRawEquals(
  columnId: string,
  raw: string,
  storedValue: unknown
): boolean {
  if (columnId === 'nodeNumber') {
    return toNodeNumberCell(raw) === toNodeNumberCell(storedValue);
  }
  if (ARRAY_INPUT_SEPARATORS[columnId]) {
    const next = raw === '' ? [] : raw.split(ARRAY_INPUT_SEPARATORS[columnId]);
    return JSON.stringify(next) === JSON.stringify(storedValue);
  }
  return raw === stringifyValue(storedValue);
}
